import {
  BOARD_CLEAR_BONUS,
  COLUMN_SIZE,
  LEVEL_UP_BONUS,
  LEVEL_UP_BONUS_BLITZ,
  MAX_DROPS,
  MIN_DROPS,
  SEQUENCE_MODE_SEED,
} from './constants';
import RNG, { IRNG, RNGState } from './rng';
import { TutorialRNG, TutorialRNGState } from './tutorial';
import {
  BoardOf,
  Bonus,
  CellValue,
  ColumnOf,
  ColumnsOf,
  DotValue,
  DropValue,
  Empty,
  GameMode,
  GameState,
  Solid,
} from './types';
import {
  getComboMultiplier,
  isCracked,
  isDotValue,
  isSolid,
  isSolidOrCracked,
  makeColumnsOf,
  solid,
  toBoard,
  toColumns,
} from './util';

// state of the game board, for serialization/deserialization
export type BoardState = {
  mode: GameMode;
  rngState: RNGState;
  cells: BoardOf<CellValue>;
  level: number;
  totalDrops: number;
  dropsRemaining: number;
  currentDrop: DropValue;
  score: number;
  combo: number;
  gameOver: boolean;
};

type OnUpdate = (data: BoardState) => void;

export type BoardOpts = {
  mode: GameMode;
  rngState?: RNGState;
  cells?: BoardOf<CellValue>;
  level?: number;
  totalDrops?: number;
  dropsRemaining?: number;
  currentDrop?: DropValue;
  score?: number;
  combo?: number;
  gameOver?: boolean;
  onUpdate?: OnUpdate;
};

// result of calling this.update
type UpdateResult = {
  didUpdate: boolean;
  bonus: Bonus;
}

export default class Board {
  readonly mode: GameMode;
  protected rng: IRNG<number | DotValue, RNGState | TutorialRNGState>;
  readonly levelUpBonus: number;
  readonly clearBonus: number;
  protected columns: ColumnsOf<CellValue>;
  protected level: number;
  protected totalDrops: number;
  protected dropsRemaining: number;
  protected currentDrop: DropValue;
  protected score: number;
  protected combo: number;
  protected gameOver: boolean;
  protected _onUpdate?: OnUpdate;

  constructor(opts: BoardOpts) {
    this.mode = opts.mode;
    if (opts.mode === GameMode.Tutorial) {
      this.rng = new TutorialRNG();
    } else if (opts.rngState) {
      this.rng = RNG.deserialize(opts.rngState);
    } else if (opts.mode === GameMode.Sequence) {
      // same starting seed every time
      this.rng = new RNG(SEQUENCE_MODE_SEED);
    } else {
      // random starting seed
      this.rng = new RNG();
    }
    this.levelUpBonus = opts.mode === GameMode.Blitz
      ? LEVEL_UP_BONUS_BLITZ
      : LEVEL_UP_BONUS;
    this.clearBonus = BOARD_CLEAR_BONUS;
    this.columns = opts.cells ? toColumns(opts.cells) : makeColumnsOf(Empty);
    this.level = opts.level ?? 1;
    this.totalDrops = opts.totalDrops ?? this.getDropCount();
    this.dropsRemaining = opts.dropsRemaining ?? this.totalDrops;
    this.currentDrop = opts.currentDrop ?? this.getNextDrop();
    this.score = opts.score ?? 0;
    this.combo = opts.combo ?? 0;
    this.gameOver = opts.gameOver ?? false;
    if (opts.mode !== GameMode.Tutorial) {
      this._onUpdate = opts.onUpdate;
    }
    if (!opts.cells) {
      this.setStartingBoard();
    }
  }

  protected clone(cloneListeners = false): Board {
    const board = new Board({
      mode: this.mode,
      cells: toBoard(this.columns),
      level: this.level,
      totalDrops: this.totalDrops,
      dropsRemaining: this.dropsRemaining,
      currentDrop: this.currentDrop,
      score: this.score,
      combo: this.combo,
      gameOver: this.gameOver,
    });
    board.rng = this.rng.clone();
    if (cloneListeners) {
      board._onUpdate = this._onUpdate;
    }
    return board;
  }

  serialize(): BoardState {
    if (this.mode === GameMode.Tutorial) {
      throw new Error('Cannot serialize tutorial state');
    }
    return {
      mode: this.mode,
      rngState: this.rng.serialize() as RNGState,
      cells: toBoard(this.columns),
      level: this.level,
      totalDrops: this.totalDrops,
      dropsRemaining: this.dropsRemaining,
      currentDrop: this.currentDrop,
      score: this.score,
      combo: this.combo,
      gameOver: this.gameOver,
    };
  }

  protected static readonly defaultHighlights = makeColumnsOf(false);
  protected static readonly defaultCellScores = makeColumnsOf(0);

  getGameState(): GameState {
    const cellScores = this.getCellScores();
    const willComboUpdate = cellScores.some(
      (columnScores) => columnScores.some((score) => score > 0)
    );
    return {
      score: this.score,
      level: this.level,
      dropsTotal: this.totalDrops,
      dropsRemaining: this.dropsRemaining,
      currentDrop: this.currentDrop,
      gameOver: this.gameOver,
      combo: willComboUpdate ? this.combo + 1 : 0,
      bonus: Bonus.None,
      columns: this.getColumns(),
      highlights: this.getHighlights(),
      cellScores,
    };
  }

  getCooldownState(): GameState {
    return {
      score: this.score,
      level: this.level,
      dropsTotal: this.totalDrops,
      dropsRemaining: this.dropsRemaining,
      currentDrop: this.currentDrop,
      gameOver: this.gameOver,
      combo: 1,
      bonus: Bonus.None,
      columns: this.getColumns(),
      highlights: Board.defaultHighlights,
      cellScores: Board.defaultCellScores,
    };
  }

  protected onUpdate() {
    if (this._onUpdate) {
      this._onUpdate(this.serialize());
    }
  }

  static getNeighborIndices(column: number, row: number): [number, number][] {
    const indices: [number, number][] = [];
    if (row > 0) indices.push([column, row - 1]); // above
    if (row < 6) indices.push([column, row + 1]); // below
    if (column > 0) indices.push([column - 1, row]); // left
    if (column < 6) indices.push([column + 1, row]); // right
    return indices;
  }

  protected setStartingBoard() {
    switch (this.mode) {
      case GameMode.Sequence:
        return this.setSequenceStartingBoard();
      case GameMode.Tutorial:
        return this.setTutorialStartingBoard();
      default:
        return this.setRandomStartingBoard();
    }
  }

  protected setSequenceStartingBoard(): void {
    this.columns[0][6] = solid(6);
    this.columns[1][6] = solid(4);
    this.columns[2][6] = solid(5);
    this.columns[3][6] = solid(7);
    this.columns[4][6] = solid(5);
    this.columns[5][6] = solid(1);
    this.columns[6][6] = solid(3);
  }

  protected setTutorialStartingBoard(): void {
    this.dropsRemaining = 5;
    //
    this.columns[0][5] = 7;
    this.columns[0][6] = 6;
    //
    this.columns[1][4] = 6;
    this.columns[1][5] = 6;
    this.columns[1][6] = 5;
    //
    this.columns[2][6] = 4;
    //
    this.columns[3][3] = 6;
    this.columns[3][4] = 7;
    this.columns[3][5] = solid(5);
    this.columns[3][6] = 6;
    //
    this.columns[4][6] = solid(5);
    //
    this.columns[5][3] = solid(1);
    this.columns[5][4] = solid(3);
    this.columns[5][5] = solid(1);
    this.columns[5][6] = solid(2);
    //
    this.columns[6][4] = 4;
    this.columns[6][5] = solid(2);
    this.columns[6][6] = 1;
  }

  /**
   * Generate a random starting board with the following rules:
   *   1. 10 to 19 starting pieces
   *   2. no cracked pieces
   *   3. randomly distributed among columns
   *   4. no column has more than 5 pieces
   *   5. board is stable (no immediate breakages occur)
   * @protected
   */
  protected setRandomStartingBoard(): void {
    if (this.mode === GameMode.Tutorial) {
      throw new Error(`setRandomStartingBoard cannot be called in Tutorial mode`);
    }
    // 1.
    const startingDrops = 20;
    for (let i = 0; i < startingDrops; i++) {
      while (true) {
        // 3.
        const column = Math.floor(this.rng.next() * COLUMN_SIZE);
        // 4.
        const row = this.getLowestEmptyRowInColumn(column);
        if (row >= 2) {
          // 2.
          this.columns[column][row] = this.getNextDrop(); // 1-8
          break;
        }
      }
    }

    // 5.
    while (true) {
      const { didUpdate } = this.tick();
      if (!didUpdate) break;
    }
    this.score = 0;
    this.combo = 0;

    // 2. (replace cracked pieces with solid)
    for (let column = 0; column < COLUMN_SIZE; column++) {
      for (let row = 0; row < COLUMN_SIZE; row++) {
        if (isCracked(this.columns[column][row])) {
          this.columns[column][row] -= 7;
        }
      }
    }
  }

  getColumns(): ColumnsOf<CellValue> {
    return [
      [...this.columns[0]],
      [...this.columns[1]],
      [...this.columns[2]],
      [...this.columns[3]],
      [...this.columns[4]],
      [...this.columns[5]],
      [...this.columns[6]],
    ];
  }

  protected setColumn(column: number, values: ColumnOf<CellValue>): void {
    this.columns[column] = values;
  }

  protected getHighlights(): ColumnsOf<boolean> {
    const highlightCells = makeColumnsOf(false);
    for (let column = 0; column < COLUMN_SIZE; column++) {
      for (let row = 0; row < COLUMN_SIZE; row++) {
        const value = this.columns[column][row];
        if (isDotValue(value)) {
          const allIndices: [number, number][] = [];
          if (this.getContiguousRowSize(column, row) === value) {
            allIndices.push(...this.getHighlightedRowColumns(column, row));
          }
          if (this.getColumnSize(column) === value) {
            allIndices.push(...this.getHighlightedColumnRows(column));
          }
          allIndices.forEach(([column, row]) => {
            highlightCells[column][row] = true;
          });
        }
      }
    }
    return highlightCells;
  }

  protected getCellScores(): ColumnsOf<number> {
    const scoreCells = makeColumnsOf(0);
    const multiplier = getComboMultiplier(this.combo + 1);
    for (let column = 0; column < COLUMN_SIZE; column++) {
      for (let row = 0; row < COLUMN_SIZE; row++) {
        const value = this.columns[column][row];
        if (isDotValue(value)) {
          if (this.getColumnSize(column) === value ||
            this.getContiguousRowSize(column, row) === value) {
            scoreCells[column][row] = multiplier;
          }
        }
      }
    }
    return scoreCells;
  }

  /**
   * Returns the row index of the lowest non-occupied cell in a column,
   * or -1 if the column is fully occupied
   * @param column - The column to search
   */
  protected getLowestEmptyRowInColumn(column: number): number {
    const col = this.columns[column];
    for (let i = 6; i >= 0; i--) {
      if (col[i] === Empty) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Get the effective size of the row at the given start cell.
   * Assumes the starting cell is non-empty.
   * @param column Column of starting cell
   * @param row Row of starting cell
   */
  protected getContiguousRowSize(column: number, row: number): number {
    let size = 1;
    // search left
    for (let c = column - 1; c >= 0; c--) {
      if (this.columns[c][row] === Empty) break;
      size++;
    }
    // search right
    for (let c = column + 1; c < COLUMN_SIZE; c++) {
      if (this.columns[c][row] === Empty) break;
      size++;
    }
    return size;
  }

  protected getColumnSize(column: number): number {
    let size = 0;
    for (let row = 6; row >= 0; row--) {
      if (this.columns[column][row] === Empty) break;
      size++;
    }
    return size;
  }

  protected getColumnSizes(): ColumnOf<number> {
    return [
      this.getColumnSize(0),
      this.getColumnSize(1),
      this.getColumnSize(2),
      this.getColumnSize(3),
      this.getColumnSize(4),
      this.getColumnSize(5),
      this.getColumnSize(6),
    ];
  }

  protected getHighlightedRowColumns(column: number, row: number): [number, number][] {
    if (this.columns[column][row] === Empty) {
      return [];
    }
    const columns = [[column, row]] as [number, number][];
    // search left
    for (let c = column - 1; c >= 0; c--) {
      if (this.columns[c][row] === Empty) break;
      columns.unshift([c, row]);
    }
    // search right
    for (let c = column + 1; c < COLUMN_SIZE; c++) {
      if (this.columns[c][row] === Empty) break;
      columns.push([c, row]);
    }
    return columns;
  }

  protected getHighlightedColumnRows(column: number): [number, number][] {
    const rows = [] as [number, number][];
    for (let row = 6; row >= 0; row--) {
      if (this.columns[column][row] === Empty) break;
      rows.unshift([column, row]);
    }
    return rows;
  }

  /**
   * Progress the board by one tick
   * - check for disappearing number pieces
   * - check for cracks/breakages
   * - drops pieces to fill gaps
   * Returns true if any update occurred, and false otherwise.
   * Note that multiple updates may be required, i.e. combos
   */
  protected tick(): UpdateResult {
    let didUpdate = false;
    let bonus = Bonus.None;
    const comboMultiplier = getComboMultiplier(this.combo);

    const colSizes = this.getColumnSizes();
    const disappearances = makeColumnsOf(false);
    const disappearanceNeighbors = makeColumnsOf(0);
    // 1. check all number cells for disappearance
    for (let column = 0; column < 7; column++) {
      for (let row = 0; row < 7; row++) {
        const value = this.columns[column][row];
        if (isDotValue(value)) {
          if (this.getContiguousRowSize(column, row) === value ||
            colSizes[column] === value) {
            disappearances[column][row] = true;
            Board.getNeighborIndices(column, row).forEach(([column, row]) => {
              disappearanceNeighbors[column][row]++;
            });
            this.score += comboMultiplier;
            didUpdate = true;
          }
        }
      }
    }
    // 2. disappear needed cells now that cells are flagged
    for (let column = 0; column < 7; column++) {
      for (let row = 0; row < 7; row++) {
        if (disappearances[column][row]) {
          this.columns[column][row] = Empty;
        }
      }
    }
    // 3. check all solid/cracked cells for crack/breakage
    for (let column = 0; column < 7; column++) {
      for (let row = 0; row < 7; row++) {
        const value = this.columns[column][row];
        if (isSolidOrCracked(value)) {
          const crackCount = disappearanceNeighbors[column][row];
          if (isSolid(value)) {
            if (crackCount === 1) {
              // Solid to Cracked
              this.columns[column][row] += 7;
              didUpdate = true;
            } else if (crackCount >= 2) {
              // Solid to DotValue
              this.columns[column][row] -= 7;
              didUpdate = true;
            }
          } else if (crackCount >= 1) {
            // Cracked to DotValue
              this.columns[column][row] -= 14;
          }
        }
      }
    }
    if (didUpdate) {
      // 4. drop pieces to fill gaps
      for (let column = 0; column < 7; column++) {
        const col = this.columns[column].filter((cell) => cell !== Empty);
        const colSize = colSizes[column];
        // was some empty cell in the middle of the column?
        if (col.length < colSize) {
          while (col.length < 7) {
            col.unshift(Empty);
          }
          this.setColumn(column, col as ColumnOf<CellValue>);
        }
      }
      // score bonus if board was fully cleared
      if (this.isBoardEmpty()) {
        this.score += this.clearBonus;
        bonus = Bonus.BoardClear;
      }
    }

    return {
      didUpdate,
      bonus,
    };
  }

  isColumnFull(column: number): boolean {
    return this.getLowestEmptyRowInColumn(column) < 0;
  }

  isBoardFull(): boolean {
    for (let column = 0; column < 7; column++) {
      if (this.getLowestEmptyRowInColumn(column) >= 0) {
        return false;
      }
    }
    return true;
  }

  isBoardEmpty(): boolean {
    return this.columns.every(
      (column) => column.every((cell) => cell === Empty)
    );
  }

  protected _drop(column: number, value: DropValue) {
    const row = this.getLowestEmptyRowInColumn(column);
    if (row < 0) {
      throw new Error(`Column ${column} is full`);
    }
    this.columns[column][row] = value;
  }

  protected _shiftUp() {
    for (let column = 0; column < 7; column++) {
      this.columns[column].shift();
      const rngRoll = this.rng.next();
      let next: Solid;
      if (this.mode === GameMode.Tutorial) {
        // TutorialRNG returns predetermined DotValues
        next = solid(rngRoll as DotValue);
      } else {
        // regular RNG returns float in range [0..1)
        // get random Solid with equal distribution of underlying DotValue
        next = solid(Math.floor(rngRoll * 7) + 1 as DotValue);
      }
      this.columns[column].push(next);
    }
  }

  protected * _doTicks(): IterableIterator<GameState> {
    while (true) {
      this.combo++;
      const { didUpdate, ...result } = this.tick();
      if (!didUpdate) {
        this.combo--;
        break;
      }
      yield {
        ...this.getGameState(),
        ...result,
      };
    }
  }

  protected * _checkBoardFull(): Generator<GameState, boolean> {
    if (this.isBoardFull()) {
      // game over
      this.gameOver = true;
      this.combo = 0;
      yield this.getGameState();
      this.onUpdate();
      return true;
    }
    return false;
  }

  * drop(column: number): IterableIterator<GameState> {
    this._drop(column, this.currentDrop);
    this.currentDrop = this.getNextDrop();
    this.dropsRemaining--;
    // briefly show the board with the drop before it's processed
    yield this.getGameState();
    // apply updates sequentially
    yield* this._doTicks();

    // if board is full, game over
    if (yield* this._checkBoardFull()) {
      return;
    }

    // if no more drops, level up and apply updates sequentially
    if (this.dropsRemaining <= 0) {
      this.level++;
      this.totalDrops = this.getDropCount();
      this.dropsRemaining = this.totalDrops;
      this.score += this.levelUpBonus;
      for (let column = 0; column < 7; column++) {
        if (this.getLowestEmptyRowInColumn(column) < 0) {
          // game over
          this.gameOver = true;
        }
      }
      this._shiftUp();
      yield {
        ...this.getGameState(),
        bonus: Bonus.LevelUp,
      };
      if (this.gameOver) {
        this.combo = 0;
        this.onUpdate();
        return;
      }
      // if board is full, game over
      if (yield* this._checkBoardFull()) {
        return;
      }
      yield* this._doTicks();
    }
    this.combo = 0;
    this.onUpdate();
  }

  protected getNextDrop(): DropValue {
    // n.b. the return value casts are mainly for clarity of the math
    const rngRoll = this.rng.next();
    if (this.mode === GameMode.Tutorial) {
      // TutorialRNG returns predetermined DotValues
      return rngRoll as DotValue;
    }
    // regular RNG returns float in range [0..1)
    if (this.mode === GameMode.Blitz) {
      // get random DotValue with equal distribution (1-7)
      return Math.floor(rngRoll * 7) + 1 as DotValue;
    }
    // get random DotValue with 7/8 chance or 'any Solid' with 1/8 chance (1-8 works here)
    const value = Math.floor(rngRoll * 8) + 1 as DotValue | Solid;
    if (value === 8) {
      // if Solid, determine underlying DotValue with equal distribution (1-7)
      // get random Solid with equal distribution (8-14)
      return solid(Math.floor(rngRoll * 7) + 1 as DotValue);
    }
    return value as DotValue;
  }

  protected getDropCount() {
    if (this.mode === GameMode.Blitz) return MIN_DROPS;
    const count = MAX_DROPS + 1 - this.level;
    return count < MIN_DROPS ? MIN_DROPS : count;
  }
}
